import { rateGuarantee } from "./sharedTypedAmount";

import type {
  TypedAmountSpec,
  TypedAmountTypeOption,
} from "../typedAmountTypes";

const NOT_INCLUDED: TypedAmountTypeOption = {
  value: "notIncluded",
  label: "Not Included",
  hideAmount: true,
};
const NOT_DISCLOSED: TypedAmountTypeOption = {
  value: "notDisclosed",
  label: "Not Disclosed",
  hideAmount: true,
};

const BENEFIT_DURATION_OPTIONS: TypedAmountTypeOption[] = [
  { value: "ssnra", label: "SSNRA", hideAmount: true },
  { value: "ssnraWithRbd", label: "SSNRA with RBD", hideAmount: true },
  { value: "rbdWithSsnra", label: "RBD with SSNRA", hideAmount: true },
  { value: "adea1WithSsnra", label: "ADEA 1 with SSNRA", hideAmount: true },
  { value: "adea1", label: "ADEA 1", hideAmount: true },
  { value: "adea2", label: "ADEA 2", hideAmount: true },
  { value: "adea3", label: "ADEA 3", hideAmount: true },
  { value: "adea4", label: "ADEA 4", hideAmount: true },
  { value: "twoYearAdea", label: "2 Year ADEA", hideAmount: true },
  { value: "threeYearAdea", label: "3 Year ADEA", hideAmount: true },
  { value: "fiveYearAdea", label: "5 Year ADEA", hideAmount: true },
  {
    value: "tenYearAdeaSchedule",
    label: "10 Year ADEA Schedule",
    hideAmount: true,
  },
  {
    value: "reducingBenefitDuration",
    label: "Reducing Benefit Duration",
    hideAmount: true,
  },
  { value: "ssAdea", label: "SS ADEA", hideAmount: true },
  { value: "toAge65", label: "To Age 65", hideAmount: true },
  { value: "toAge67", label: "To Age 67", hideAmount: true },
  {
    value: "laterOfAgeOrSsnra",
    label: "Later of Age or SSNRA",
    hideAmount: true,
  },
  { value: "lesserOfRbdOrNumberOfYears", label: "Lesser of RBD or Years" },
  { value: "yearDuration", label: "Years" },
  NOT_INCLUDED,
  NOT_DISCLOSED,
];

const MENTAL_ILLNESS_OPTIONS: TypedAmountTypeOption[] = [
  { value: "years", label: "Years" },
  { value: "months", label: "Months" },
  { value: "noLimit", label: "No Limit", hideAmount: true },
  {
    value: "payableSameAsAnyOtherDisability",
    label: "Payable Same as Any Other Disability",
    hideAmount: true,
  },
  { value: "included", label: "Included", hideAmount: true },
  NOT_INCLUDED,
  NOT_DISCLOSED,
];

// Plan design pairs a dollar amount (the primary amount) with a percentage
// (an extra input); the selected type governs which show. IDI `percentage`
// shows only the percent; LTD `increments` shows only the dollar; both
// `percentageToDollarAmount` show both. Mirrors CI's benefit maximum.
const IDI_PLAN_DESIGN_OPTIONS: TypedAmountTypeOption[] = [
  { value: "percentage", label: "Percentage", hideAmount: true },
  { value: "percentageToDollarAmount", label: "Percentage to Dollar Amount" },
  NOT_INCLUDED,
  NOT_DISCLOSED,
];

const LTD_PLAN_DESIGN_OPTIONS: TypedAmountTypeOption[] = [
  { value: "increments", label: "Increments" },
  { value: "percentageToDollarAmount", label: "Percentage to Dollar Amount" },
  NOT_INCLUDED,
  NOT_DISCLOSED,
];

const IDI_PLAN_DESIGN_PERCENT_TYPES = [
  "percentage",
  "percentageToDollarAmount",
];
const LTD_PLAN_DESIGN_PERCENT_TYPES = ["percentageToDollarAmount"];

export const idiTypedAmountFields: Record<string, TypedAmountSpec> = {
  idiPlanDesign: {
    typeKey: "idiPlanDesignType",
    amountKey: "idiPlanDesignDollarAmount",
    amountLabel: "Dollar Amount",
    typeOptions: IDI_PLAN_DESIGN_OPTIONS,
    extraKeys: [
      {
        key: "idiPlanDesignPercent",
        label: "Percentage",
        showForTypes: IDI_PLAN_DESIGN_PERCENT_TYPES,
        max: 100,
      },
    ],
  },
  ltdPlanDesign: {
    typeKey: "ltdPlanDesignType",
    amountKey: "ltdPlanDesignDollarAmount",
    amountLabel: "Dollar Amount",
    typeOptions: LTD_PLAN_DESIGN_OPTIONS,
    extraKeys: [
      {
        key: "ltdPlanDesignPercent",
        label: "Percentage",
        showForTypes: LTD_PLAN_DESIGN_PERCENT_TYPES,
        max: 100,
      },
    ],
  },
  ownOccupationPeriod: {
    typeKey: "ownOccupationPeriodType",
    amountKey: "ownOccupationPeriodAmount",
    amountLabel: "Amount",
    amountMin: 1,
    amountInteger: true,
    typeOptions: [
      { value: "monthsOwnOccupation", label: "Months Own Occupation" },
      { value: "yearsOwnOccupation", label: "Years Own Occupation" },
      {
        value: "ownOccupationOnly",
        label: "Own Occupation Only",
        hideAmount: true,
      },
      NOT_INCLUDED,
      NOT_DISCLOSED,
    ],
  },
  benefitDuration: {
    typeKey: "benefitDurationType",
    amountKey: "benefitDurationAmount",
    amountLabel: "Amount",
    typeOptions: BENEFIT_DURATION_OPTIONS,
  },
  mentalIllnessBenefit: {
    typeKey: "mentalIllnessBenefitType",
    amountKey: "mentalIllnessBenefitTimeframe",
    amountLabel: "Timeframe",
    typeOptions: MENTAL_ILLNESS_OPTIONS,
  },
  recoveryBenefit: {
    typeKey: "recoveryBenefitType",
    amountKey: "recoveryBenefitAmount",
    amountLabel: "Amount",
    amountInteger: true,
    typeOptions: [
      { value: "dollarAmount", label: "Dollar amount" },
      { value: "included", label: "Included", hideAmount: true },
      NOT_INCLUDED,
      NOT_DISCLOSED,
    ],
  },
  catastrophicBenefit: {
    typeKey: "catastrophicBenefitType",
    amountKey: "catastrophicBenefitAmount",
    amountLabel: "Percentage",
    amountInteger: true,
    typeOptions: [
      { value: "percentageAmount", label: "Percentage", amountMax: 100 },
      { value: "included", label: "Included", hideAmount: true },
      NOT_INCLUDED,
      NOT_DISCLOSED,
    ],
  },
  rateGuarantee,
};
